import { Directions } from '../components/core/types/globalTypes';
import { isNullOrUndefined } from './collection';

/**
 * Goes through the provided list of elements recursively and returns the underlying content from slot elements
 * @param {HTMLElement[]} slotElements  A list of elements to go through
 * @returns {HTMLElement[]}             The underlying content
 */
export const getContentFromSlotElementsRecursively = (slotElements: HTMLElement[]): HTMLElement[] => {
  return slotElements.reduce((acc: HTMLElement[], currentValue) => {
    if (currentValue.tagName && currentValue.tagName.toLowerCase() === 'slot') {
      const tmpSlotElement = currentValue as HTMLSlotElement;
      return getContentFromSlotElementsRecursively(tmpSlotElement.assignedElements() as HTMLElement[]);
    }
    acc.push(currentValue);
    return acc;
  }, []);
};

/**
 * Going up the chain of elements, it will return true if the given element is a child of the root node and false if it sits under an other (slotted) element
 * @param {HTMLElement} componentEl     The root element (parent)
 * @param {HTMLElement} element         The element in question
 * @param {number} maxIterations        Optional. Number of steps to find the parent. Default: 10
 * @returns {boolean}                   Whether or not the given element is a child of the root node
 */
const isElementChildOfComponent = (componentEl: HTMLElement, element: HTMLElement, maxIterations = 10): boolean => {
  {
    let tmpElement = element;
    for (let index = 0; index < maxIterations; index++) {
      if (tmpElement.parentElement === componentEl) {
        return true;
      }
      if (!tmpElement.parentElement || tmpElement.parentElement.getAttribute('slot')) {
        return false;
      }

      tmpElement = tmpElement.parentElement;
    }
  }
};

/**
 * Returns the content elements of the slots within a component
 * If browser doesn't support slots, it will return all the elements that are assigned to the space where the slot should be
 * @param {HTMLElement} element         The parent element
 * @param {string} slotName             Optional slot name for filtering results
 * @returns {Element[]}                 The content elements of the slots within a component
 */
export const getSlottedElements = (element: HTMLElement, slotName = ''): Element[] => {
  const selector = slotName ? `slot[name=${slotName}]` : 'slot';

  return getContentFromSlotElementsRecursively(
    Array.from(element.shadowRoot.querySelectorAll(selector))
      .map((slotElement: HTMLSlotElement) => slotElement.assignedNodes() as HTMLElement[])
      .flat()
  );
};

/**
 * Returns all the child elements within a slot property, regardless of them being in an actual slot or not
 * @param {HTMLElement} element         The parent element
 * @param {string} slotName             Optional slot name for filtering results
 * @returns {Element[]}                 The child elements within a slot
 */
export const getAllElementsWithSlotAttribute = (element: HTMLElement, slotName = ''): Element[] => {
  const selector = slotName ? `[slot=${slotName}]` : '[slot]';
  return Array.from(element.querySelectorAll(selector)).filter((el: HTMLElement) => isElementChildOfComponent(element, el));
};

/**
 * Going up the chain of elements, it returns the first element with the given attribute
 * @param {HTMLElement} el              The src element
 * @param {string} attribute            The attribute to look for
 * @param {string} attributeValue       The attribute value to look for
 * @param {number} maxIterations        Max iterations. Default 10
 * @returns {HTMLElement}               The first element with the given attribute
 */
export const getFirstParentWithAttribute = (el: HTMLElement, attribute: string, attributeValue: string, maxIterations = 10): HTMLElement => {
  if (el.getAttribute(attribute) === attributeValue) {
    return el;
  }
  let currentEl = el;
  let currentIteration = 0;
  while (currentEl && currentEl.parentElement && currentIteration <= maxIterations) {
    const parentAttr = currentEl.parentElement.getAttribute(attribute);
    if (parentAttr && parentAttr.toString().toLowerCase() === attributeValue.toString().toLowerCase()) {
      return currentEl.parentElement;
    }
    currentEl = currentEl.parentElement;
    currentIteration++;
  }
  return null;
};

/**
 * Starting from the given element, goes upwards in the dom accounting for slots and shadowRoot, it will return if the given element sits under an other element
 * @param {Element} el1 The descendant element
 * @param {Element} el2 The parent element
 * @param {number} maxIterations max iterations. Default = 20
 * @returns {boolean} True if element one is a descendant (in the DOM) of element two
 */
export const getIsElementDescendantOfElement = (el1: Element, el2: Element, maxIterations = 20): boolean => {
  const getParentInDom = (el: Element): Element => {
    // if it's under a slot, go to the slot
    if (el.assignedSlot) {
      return el.assignedSlot;
      // if not under slot get the normal parent
    } else if (el.parentElement) {
      return el.parentElement;
      // if there is no parent try and find the shadowroot host that contains the element
    } else if (el.getRootNode()) {
      const rootNode = el.getRootNode() as ShadowRoot;
      return rootNode.host;
    }
  };

  let currentIteration = 0;
  let currentParent: Element = getParentInDom(el1);

  while (currentParent && currentIteration <= maxIterations) {
    if (currentParent === el2) {
      return true;
    }
    currentParent = getParentInDom(currentParent);
    currentIteration++;
  }
  return false;
};

/**
 * Given an element, calculate whether it should render in RTL mode
 * @param {Element} elem The element in question
 * @returns {boolean} Whether the element should render in RTL mode
 */
export const getIsElementRTL = (elem: Element): boolean => {
  return (
    elem.getAttribute('dir') === Directions.RTL ||
    (document.documentElement.getAttribute('dir') === Directions.RTL && elem.getAttribute('dir') !== Directions.LTR)
  );
};

/**
 * Sets a MutationObserver to detect dir change on the HTML element
 * @param {Function} onDirMutationCallback Callback to be executed when dir changes
 * @returns {MutationObserver} The mutation observer obj
 */
export const getHtmlDirObserver = (onDirMutationCallback: (htmlDirMutation?: MutationRecord) => void): MutationObserver => {
  const config = { attributes: true, childList: false, subtree: false };
  const obs = new MutationObserver((mutationList) => {
    const htmlDirMutation = mutationList.find((ml) => ml.target.nodeName.toLowerCase() === 'html' && ml.attributeName === 'dir');
    if (!isNullOrUndefined(htmlDirMutation)) {
      onDirMutationCallback(htmlDirMutation);
    }
  });
  obs.observe(document.documentElement, config);
  return obs;
};
